import { Injectable, Injector, inject, signal } from '@angular/core';
import { BaseService } from '@core/services/base.service';
import { HttpClient } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { LoginModel } from '@core/models/login.model';
import { tap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { UserService } from '@core/services/user.service';
import { Subscription } from 'rxjs';
import { BusquedaGlobalService } from '@shared/services/busqueda-global.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService<LoginModel> {
  private userService = inject(UserService);
  private injector = inject(Injector);

  loginForm!: FormGroup;

  /** Roles derivados del JWT (claim `roles[]` o `role`). */
  private readonly userRolesSignal = signal<string[]>([]);

  private token: string | null = null;
  private refreshToken: string | null = null;
  private tokenRefreshSubscription: Subscription | null = null;
  private readonly ONE_MINUTE = 60 * 1000;
  private readonly ONE_HOUR = 60 * this.ONE_MINUTE;
  private readonly REFRESH_INTERVAL = 6 * this.ONE_HOUR;
  private readonly TOKEN_KEY = 'token';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private apiUrl = environment.apiUrl;

  constructor(http: HttpClient) {
    super(http, 'authentication/Login');
    this.initializeForm();
    this.loadSessionFromStorage();
  }

  initializeForm() {
    this.loginForm = new FormGroup({
      username: new FormControl<string | null>(null, Validators.required),
      password: new FormControl<string | null>(null, Validators.required),
    });
  }

  private loadSessionFromStorage() {
    this.token = localStorage.getItem(this.TOKEN_KEY);
    this.refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (this.token) {
      this.decodeAndSetRolesFromToken(this.token);
    }
  }

  getLoginForm(): FormGroup {
    return this.loginForm;
  }

  resetForm(): void {
    this.loginForm.reset();
  }

  login(): Observable<LoginModel> {
    // El login exitoso no debe mostrar toast genérico de BaseService.
    return this.create({ ...this.loginForm.value }, 'Login', false);
  }

  setSession(token: string, refreshToken: string): void {
    this.token = token;
    this.refreshToken = refreshToken;
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);

    this.decodeAndSetRolesFromToken(token);
  }

  getToken(): string | null {
    const localToken = localStorage.getItem(this.TOKEN_KEY);
    this.token = localToken;
    return localToken;
  }

  refreshAccessToken(): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/authentication/refresh-token`, {
        refreshToken: localStorage.getItem(this.REFRESH_TOKEN_KEY),
      })
      .pipe(
        tap((response: any) => {
          // Mantener el mismo contrato que `lrp_frontend`:
          // el backend retorna el token y campos de usuario directamente.
          this.setSession(response.accessToken, response.refreshToken);
          this.userService.setUserData({
            email: response?.userName,
            fullName: response?.fullName,
            mainRole: response?.jobTitle,
            phone: response?.mobilePhone,
          });
        }),
      );
  }

  startTokenRefresh(): void {
    this.stopTokenRefresh();
    this.tokenRefreshSubscription = timer(
      this.REFRESH_INTERVAL,
      this.REFRESH_INTERVAL,
    )
      .pipe(switchMap(() => this.refreshAccessToken()))
      .subscribe({
        next: () => console.log('Token renovado por intervalo.'),
        error: () => this.logout(),
      });
  }

  stopTokenRefresh() {
    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
      this.tokenRefreshSubscription = null;
    }
  }

  logout() {
    this.stopTokenRefresh();
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.userService.clearCompanyId();
    this.userService.clearProveedorId();
    this.userService.clearUserName();
    this.userService.clearUserData();
    // Resultados del buscador del usuario saliente. Se resuelve aquí y no en
    // el constructor: el interceptor crea AuthService y no debe arrastrar al buscador.
    this.injector.get(BusquedaGlobalService).limpiarCache();

    this.userRolesSignal.set([]);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Retorna `true` si el JWT incluye el rol solicitado.
   * Soporta claims:
   * - `roles`: string[]
   * - `role`: string
   */
  hasRole(role: string): boolean {
    return this.userRolesSignal().includes(role);
  }

  private decodeAndSetRolesFromToken(token: string): void {
    const payload = this.decodeJwtPayload(token);
    const roles = this.extractRolesFromPayload(payload);
    this.userRolesSignal.set(roles);
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '==='.slice(0, (4 - (base64.length % 4)) % 4);
      const jsonPayload = atob(padded);

      const parsed = JSON.parse(jsonPayload) as Record<string, unknown>;
      return parsed ?? null;
    } catch {
      return null;
    }
  }

  private extractRolesFromPayload(
    payload: Record<string, unknown> | null,
  ): string[] {
    if (!payload) return [];

    const rolesCandidate = (payload as Record<string, unknown>)['roles'];
    if (Array.isArray(rolesCandidate)) {
      return rolesCandidate.filter((r): r is string => typeof r === 'string');
    }

    const roleCandidate = (payload as Record<string, unknown>)['role'];
    if (typeof roleCandidate === 'string') {
      return [roleCandidate];
    }

    return [];
  }
}
